// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import sortBy from 'lodash/sortBy'
import React, { useContext, useMemo, useState } from 'react'

import { required, validate } from 'lib-common/form-validation'
import type {
  ApplicationType,
  PaperApplicationCreateRequest
} from 'lib-common/generated/api-types/application'
import type { CreatePersonBody } from 'lib-common/generated/api-types/pis'
import type { PersonJSON } from 'lib-common/generated/api-types/pis'
import type { PersonId } from 'lib-common/generated/api-types/shared'
import LocalDate from 'lib-common/local-date'
import { formatPersonName } from 'lib-common/names'
import { useMutationResult } from 'lib-common/query'
import Select from 'lib-components/atoms/dropdowns/Select'
import Checkbox from 'lib-components/atoms/form/Checkbox'
import Radio from 'lib-components/atoms/form/Radio'
import { FixedSpaceColumn } from 'lib-components/layout/flex-helpers'
import { PersonName } from 'lib-components/molecules/PersonNames'
import DatePicker from 'lib-components/molecules/date-picker/DatePicker'
import FormModal from 'lib-components/molecules/modals/FormModal'
import { Label } from 'lib-components/typography'
import { applicationTypes } from 'lib-customizations/employee'
import { faFileAlt } from 'lib-icons'

import { getEmployeeUrlPrefix } from '../../constants'
import { useTranslation } from '../../state/i18n'
import { UIContext } from '../../state/ui'
import { errorToInputInfo } from '../../utils/validation/input-info-helper'
import CreatePersonInput from '../common/CreatePersonInput'
import {
  DbPersonSearch as PersonSearch,
  VtjPersonSearch
} from '../common/PersonSearch'

import { createPaperApplicationMutation } from './queries'

type PersonType = 'GUARDIAN' | 'DB_SEARCH' | 'VTJ' | 'NEW_NO_SSN'

const personToSelectOption = (person: PersonJSON) => ({
  name: formatPersonName(person, 'First Last'),
  id: person.id
})

const hasContent = (s: string | undefined): s is string =>
  typeof s === 'string' && s.length > 0

interface CreateApplicationModalProps {
  child: PersonJSON
  guardians: PersonJSON[]
}

function CreateApplicationModal({
  child,
  guardians
}: CreateApplicationModalProps) {
  const { i18n } = useTranslation()
  const i18nView = i18n.childInformation.application.create
  const { clearUiMode } = useContext(UIContext)
  const { mutateAsync: createPaperApplication } = useMutationResult(
    createPaperApplicationMutation
  )

  const sortedGuardians = useMemo(
    () => sortBy(guardians, ['lastName', 'firstName']),
    [guardians]
  )

  const [personType, setPersonType] = useState<PersonType>(
    sortedGuardians.length > 0 ? 'GUARDIAN' : 'DB_SEARCH'
  )
  const [guardian, setGuardian] = useState(
    sortedGuardians.length > 0 ? personToSelectOption(sortedGuardians[0]) : null
  )
  const [personId, setPersonId] = useState<PersonId | undefined>(undefined)
  const [newVtjPersonSsn, setNewVtjPersonSsn] = useState<string | undefined>(
    undefined
  )

  const [type, setType] = useState<ApplicationType>('DAYCARE')
  const [sentDate, setSentDate] = useState<LocalDate | null>(
    LocalDate.todayInSystemTz()
  )
  const [hideFromGuardian, setHideFromGuardian] = useState(false)
  const [createPersonBody, setCreatePersonInfo] = useState<
    Partial<CreatePersonBody>
  >({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  const validCreatePersonInfo: CreatePersonBody | null = useMemo(() => {
    const {
      dateOfBirth,
      firstName,
      lastName,
      phone,
      streetAddress,
      postalCode,
      postOffice
    } = createPersonBody
    if (
      !!dateOfBirth &&
      hasContent(firstName) &&
      hasContent(lastName) &&
      hasContent(phone) &&
      hasContent(streetAddress) &&
      hasContent(postalCode) &&
      hasContent(postOffice)
    ) {
      return {
        dateOfBirth,
        firstName,
        lastName,
        phone,
        streetAddress,
        postalCode,
        postOffice,
        email: createPersonBody.email ?? null
      }
    } else {
      return null
    }
  }, [createPersonBody])

  function canSubmit() {
    if (isSubmitting) return false
    switch (personType) {
      case 'GUARDIAN':
        return !!guardian
      case 'DB_SEARCH':
        return !!personId && personId !== child.id
      case 'VTJ':
        return !!newVtjPersonSsn
      case 'NEW_NO_SSN':
        return validCreatePersonInfo !== null
      default:
        return false
    }
  }

  function submit() {
    if (!canSubmit() || sentDate == null) return

    const commonBody: PaperApplicationCreateRequest = {
      childId: child.id,
      type,
      sentDate,
      hideFromGuardian,
      guardianId: null,
      guardianSsn: null,
      guardianToBeCreated: null,
      transferApplication: false
    }

    const apiCall =
      personType === 'GUARDIAN'
        ? () =>
            createPaperApplication({
              body: {
                ...commonBody,
                guardianId: guardian?.id ?? null
              }
            })
        : personType === 'DB_SEARCH'
          ? () =>
              createPaperApplication({
                body: {
                  ...commonBody,
                  guardianId: personId ?? null
                }
              })
          : personType === 'VTJ'
            ? () =>
                createPaperApplication({
                  body: {
                    ...commonBody,
                    guardianSsn: newVtjPersonSsn ?? null
                  }
                })
            : personType === 'NEW_NO_SSN' && !!validCreatePersonInfo
              ? () =>
                  createPaperApplication({
                    body: {
                      ...commonBody,
                      guardianToBeCreated: validCreatePersonInfo
                    }
                  })
              : null

    if (apiCall) {
      setIsSubmitting(true)

      void apiCall()
        .then((id) => {
          if (id.isSuccess) {
            window.location.href = `${getEmployeeUrlPrefix()}/employee/applications/${
              id.value
            }?create=true`
          }
        })
        .finally(() => setIsSubmitting(false))
    }
  }

  return (
    <FormModal
      title={i18nView.modalTitle}
      icon={faFileAlt}
      type="info"
      resolveAction={submit}
      resolveLabel={i18nView.createButton}
      resolveDisabled={!canSubmit()}
      rejectAction={clearUiMode}
      rejectLabel={i18n.common.cancel}
    >
      <FixedSpaceColumn $spacing="L">
        <div>
          <Label>
            <PersonName person={child} format="Last First" />
          </Label>
          <div>{child.socialSecurityNumber || child.dateOfBirth.format()}</div>
          <div>{`${child.streetAddress ?? ''}, ${child.postalCode ?? ''} ${
            child.postOffice ?? ''
          }`}</div>
        </div>

        <div>
          <FixedSpaceColumn>
            <Label>{i18nView.applier}</Label>

            {sortedGuardians.length > 0 && (
              <div>
                <Radio
                  checked={personType === 'GUARDIAN'}
                  label={i18nView.personTypes.GUARDIAN}
                  onChange={() => setPersonType('GUARDIAN')}
                  data-qa="select-guardian-radio"
                />
                <div>
                  <Select
                    items={sortedGuardians.map(personToSelectOption)}
                    onChange={setGuardian}
                    selectedItem={guardian}
                    onFocus={() => setPersonType('GUARDIAN')}
                    data-qa="select-guardian"
                    getItemValue={({ id }) => id}
                    getItemLabel={({ name }) => name}
                    getItemDataQa={({ name }) => `guardian-${name}`}
                  />
                </div>
              </div>
            )}

            <div>
              <Radio
                checked={personType === 'DB_SEARCH'}
                label={i18nView.personTypes.DB_SEARCH}
                onChange={() => setPersonType('DB_SEARCH')}
              />
              <PersonSearch
                onResult={(res) => setPersonId(res?.id)}
                onFocus={() => setPersonType('DB_SEARCH')}
                ageAtLeast={10}
                excludePeople={[child.id]}
              />
            </div>

            <div>
              <Radio
                checked={personType === 'VTJ'}
                label={i18nView.personTypes.VTJ}
                onChange={() => setPersonType('VTJ')}
                data-qa="vtj-person-radio"
              />
              <VtjPersonSearch
                data-qa="select-search-from-vtj-guardian"
                ageAtLeast={10}
                excludePeople={[child.id]}
                onResult={(res) =>
                  setNewVtjPersonSsn(res?.socialSecurityNumber || undefined)
                }
                onFocus={() => setPersonType('VTJ')}
              />
            </div>

            <div>
              <Radio
                data-qa="radio-new-no-ssn"
                checked={personType === 'NEW_NO_SSN'}
                label={i18nView.personTypes.NEW_NO_SSN}
                onChange={() => setPersonType('NEW_NO_SSN')}
              />
              <CreatePersonInput
                createPersonInfo={createPersonBody}
                setCreatePersonInfo={setCreatePersonInfo}
                personType={personType}
                onFocus={() => setPersonType('NEW_NO_SSN')}
              />
            </div>
          </FixedSpaceColumn>
        </div>

        <div>
          <Label>{i18nView.applicationType}</Label>
          <div>
            <Select
              data-qa="select-application-type"
              items={applicationTypes}
              onChange={(value) => (value ? setType(value) : undefined)}
              selectedItem={type}
              getItemLabel={(type) => i18nView.applicationTypes[type]}
            />
          </div>
        </div>

        <div>
          <Label>{i18nView.sentDate}</Label>
          <div>
            <DatePicker
              date={sentDate}
              onChange={setSentDate}
              required
              info={errorToInputInfo(
                validate(sentDate, required),
                i18n.validationErrors
              )}
              locale="fi"
            />
          </div>
        </div>

        <FixedSpaceColumn>
          <Checkbox
            checked={hideFromGuardian}
            label={i18nView.hideFromGuardian}
            onChange={(checked) => setHideFromGuardian(checked)}
          />
        </FixedSpaceColumn>
      </FixedSpaceColumn>
    </FormModal>
  )
}

export default CreateApplicationModal
